/**
 * What a decommission did about the credentials a device read with, in the
 * Edge Fleet drawer (release 3.1 fleet wave 2a).
 *
 * A retired agent must not keep what it read with:
 *   - a decision gateway or DSAR actuation connector: the controller REVOKED
 *     its service credential in the same act, so the card says which one;
 *   - a DAM collector: it read customer databases and cloud audit streams with
 *     accounts only the customer can revoke, and wrote with the tenant's
 *     ClickHouse writer. The controller flags them `required` and names the
 *     reader accounts the collector reported (names, never secrets); the card
 *     lists them and an operator confirms, with a reason, once they are
 *     revoked (POST /v1/edge/fleet/:id/credentials-revoked, audited). The
 *     server answers 409 when nothing is waiting, and the card shows its words.
 *
 * `detail` is the record the controller wrote; it is read defensively, because
 * it is JSON off the wire.
 */
import { useState } from 'react';
import { CheckCircle2, KeyRound, Loader2 } from 'lucide-react';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { confirmEdgeCredentialsRevoked, problemDetail, type EdgePodDetail } from '@/lib/api';
import { edgeAgentTypeLabel, inSentence } from '@/lib/edge-agent-types';

interface Props {
  pod: EdgePodDetail;
  onConfirmed: (pod: EdgePodDetail) => void;
}

export interface RevocationReader {
  name: string | null;
  mode: string | null;
  account: string | null;
  host: string | null;
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v : null;
}

/** The reader accounts a DAM collector reported; null = it did not report them (older than 3.1). */
export function revocationReaders(detail: Record<string, unknown> | null | undefined): RevocationReader[] | null {
  const raw = detail && Object.hasOwn(detail, 'readers') ? detail['readers'] : null;
  if (!Array.isArray(raw)) return null;
  return raw
    .filter((r): r is Record<string, unknown> => typeof r === 'object' && r !== null)
    .map((r) => ({ name: str(r['name']), mode: str(r['mode']), account: str(r['account']), host: str(r['host']) }));
}

function field(detail: Record<string, unknown> | null | undefined, key: string): unknown {
  return detail && Object.hasOwn(detail, key) ? detail[key] : undefined;
}

export const CONFIRM_REASON_MIN = 3;
export const CONFIRM_REASON_MAX = 250;

export function EdgePodCredentialsRevocation({ pod, onConfirmed }: Props): JSX.Element | null {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const rev = pod.credentialsRevocation;
  if (!rev) return null;
  const detail = rev.detail;
  const type = inSentence(edgeAgentTypeLabel(pod));

  const confirm = async () => {
    const r = reason.trim();
    if (r.length < CONFIRM_REASON_MIN) return;
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const out = await confirmEdgeCredentialsRevoked(pod.id, r.slice(0, CONFIRM_REASON_MAX));
      setReason('');
      setDone('Recorded: the credentials are confirmed revoked.');
      onConfirmed(out.pod);
    } catch (e) {
      setError(problemDetail(e, 'the confirmation was refused'));
    } finally {
      setBusy(false);
    }
  };

  const at = rev.at ? new Date(rev.at).toLocaleString() : null;
  const readers = revocationReaders(detail);
  const writer = str(field(detail, 'clickhouseWriter'));
  const instruction = str(field(detail, 'instruction'));

  const readerList =
    readers && readers.length > 0 ? (
      <ul className="space-y-1" data-testid="revocation-readers">
        {readers.map((r, i) => (
          <li key={`${r.name ?? 'reader'}-${i}`} className="glass-lite rounded p-2">
            <span className="font-medium text-foreground">{r.name ?? 'unnamed reader'}</span>
            {r.mode ? <span className="text-muted-foreground"> ({r.mode})</span> : null}
            <div className="text-muted-foreground">
              Account:{' '}
              {r.account ? (
                <code className="font-mono text-[11px] text-foreground break-all">{r.account}</code>
              ) : (
                <span className="italic">not reported</span>
              )}
              {r.host ? (
                <>
                  {' '}on <code className="font-mono text-[11px] text-foreground break-all">{r.host}</code>
                </>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    ) : readers && readers.length === 0 ? (
      <p className="text-muted-foreground">The collector reported no reader accounts.</p>
    ) : null;

  return (
    <GlassCard
      className={`p-3 ${rev.state === 'required' ? 'border border-warning/40' : ''}`}
      data-testid="credentials-revocation"
    >
      <CardHeader className="p-0 mb-2">
        <CardTitle className="text-sm inline-flex items-center gap-1.5">
          <KeyRound className="h-4 w-4" /> Credentials
          {rev.state === 'required' ? (
            <Badge variant="warning" className="text-[10px]">revoke required</Badge>
          ) : rev.state === 'revoked' ? (
            <Badge variant="success" className="text-[10px]">revoked by the controller</Badge>
          ) : rev.state === 'confirmed' ? (
            <Badge variant="success" className="text-[10px]">confirmed revoked</Badge>
          ) : (
            <Badge variant="glass" className="text-[10px]">{String(rev.state)}</Badge>
          )}
        </CardTitle>
        <CardDescription className="text-[11px]">
          {rev.state === 'revoked'
            ? `When this ${type} was decommissioned${at ? ` (${at})` : ''}, the controller revoked its service credential, so it is refused on every call.`
            : rev.state === 'required'
              ? `This ${type} was decommissioned${at ? ` (${at})` : ''}, but the accounts it read with can only be revoked on your own systems. Revoke them, then confirm here.`
              : `An operator confirmed${at ? ` on ${at}` : ''} that the credentials this ${type} read with were revoked.`}
        </CardDescription>
      </CardHeader>

      <div className="space-y-2 text-xs">
        {rev.state === 'revoked' ? (
          <dl className="grid grid-cols-1 gap-2 md:grid-cols-3" data-testid="revoked-credential">
            <div>
              <dt className="text-muted-foreground">Service credential</dt>
              <dd>
                {str(field(detail, 'serviceCredentialId')) ? (
                  <code className="font-mono text-[11px] break-all">{str(field(detail, 'serviceCredentialId'))}</code>
                ) : (
                  <span className="italic text-muted-foreground">not recorded</span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Purpose</dt>
              <dd>{str(field(detail, 'purpose')) ?? <span className="italic text-muted-foreground">not recorded</span>}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Instance label</dt>
              <dd>
                {str(field(detail, 'instanceLabel')) ?? <span className="italic text-muted-foreground">not recorded</span>}
              </dd>
            </div>
            {field(detail, 'alreadyRevoked') === true ? (
              <p className="text-muted-foreground md:col-span-3">It had already been revoked before the decommission.</p>
            ) : null}
          </dl>
        ) : (
          <>
            {readers === null && rev.state === 'required' ? (
              <p className="text-foreground" data-testid="revocation-instruction">
                {instruction ??
                  'This collector did not report its reader accounts: revoke the database and cloud accounts named in its deployment.'}
              </p>
            ) : (
              <>
                {readerList}
                {instruction && rev.state === 'required' ? <p className="text-foreground">{instruction}</p> : null}
              </>
            )}
            {writer ? (
              <p className="text-muted-foreground">
                <span className="font-medium text-foreground">ClickHouse writer: </span>
                {writer}
              </p>
            ) : null}
          </>
        )}

        {rev.state === 'required' ? (
          <div className="flex flex-wrap items-end gap-2 pt-1">
            <div className="min-w-48 flex-1">
              <label htmlFor="credentials-revoked-reason" className="mb-0.5 block text-muted-foreground">
                Reason for confirming (recorded in the audit log)
              </label>
              <input
                id="credentials-revoked-reason"
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="reader accounts disabled by the DBA team"
                maxLength={CONFIRM_REASON_MAX}
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40 focus:border-primary focus:outline-none"
              />
            </div>
            <Button
              size="sm"
              onClick={() => void confirm()}
              disabled={busy || reason.trim().length < CONFIRM_REASON_MIN}
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
              Confirm revoked
            </Button>
          </div>
        ) : null}

        {error ? (
          <p className="text-destructive" role="alert">
            {error}
          </p>
        ) : null}
        {done ? <p className="text-foreground">{done}</p> : null}
      </div>
    </GlassCard>
  );
}
